import { useEffect, useRef } from "react";
import type { AgentUIMessage } from "@/lib/ai/types";

/**
 * 再同期判定に使う、メッセージ単位の安定したスナップショットを生成する。
 *
 * message ID が同一でも UI 表示上は差分になりうるため、
 * `parts` と `metadata` を比較対象に含める。
 */
function snapshotMessage(message: AgentUIMessage): string {
  const { id, role, metadata, parts } = message;
  return JSON.stringify({ id, role, metadata, parts });
}

/**
 * メッセージ配列全体の比較用シグネチャを生成する。
 * 同期判定は配列順序込みで評価するため、要素単位スナップショットを連結する。
 */
function buildMessagesSignature(messages: AgentUIMessage[]): string {
  return messages.map((message) => snapshotMessage(message)).join(",");
}

/**
 * `useInitialMessageSync` の入力。
 *
 * この Hook は「外部初期値」と「useChat ローカル状態」の同期境界を管理するため、
 * chat 識別子・メッセージ本体・永続化進行状況を同時に受け取る。
 */
type UseInitialMessageSyncParams = {
  activeChatId?: string | null;
  pendingCreatedChatId: string | null;
  effectiveInitialMessages: AgentUIMessage[];
  pendingPersistenceCount: number;
  invalidateQueue: (error: unknown) => void;
  setMessages: (messages: AgentUIMessage[]) => void;
  resetPersistenceTracking: (messages: AgentUIMessage[]) => void;
};

/**
 * `useChat` のローカルメッセージと外部から渡される初期メッセージを同期する。
 *
 * 挙動:
 * - chat コンテキストが変わった場合は、保留中の永続化待機を即時無効化する。
 * - 初回保存した chat の取得待ちで初期値が空になった場合は、保存済みの会話を保持する。
 * - 同一 chat で永続化が進行中の間に内容差分だけが発生した場合は、
 *   楽観的 UI を巻き戻さないよう同期を遅延させる。
 * - それ以外は、メッセージ本体と永続化トラッキングを再初期化する。
 */
export function useInitialMessageSync({
  activeChatId,
  pendingCreatedChatId,
  effectiveInitialMessages,
  pendingPersistenceCount,
  invalidateQueue,
  setMessages,
  resetPersistenceTracking,
}: UseInitialMessageSyncParams) {
  const lastInitIdRef = useRef<string | null>(activeChatId ?? null);
  const lastMsgSignatureRef = useRef(
    buildMessagesSignature(effectiveInitialMessages),
  );

  useEffect(() => {
    const currentMsgSignature = buildMessagesSignature(
      effectiveInitialMessages,
    );
    const syncChatId = activeChatId ?? null;
    // draft -> persisted 直後は同一フローの遷移なので、chat 切替として扱わない。
    const isPendingCreated = syncChatId && syncChatId === pendingCreatedChatId;
    const hasChatIdChanged =
      syncChatId !== lastInitIdRef.current && !isPendingCreated;
    const hasMessageSignatureChanged =
      currentMsgSignature !== lastMsgSignatureRef.current;
    const hasInFlightPersistence = pendingPersistenceCount > 0;

    if (isPendingCreated && effectiveInitialMessages.length === 0) {
      // 初回保存後の取得待ちは空の履歴ではない。保存した会話を保持しつつ、
      // propsの引き継ぎだけを完了させ、取得結果が届いた時に通常どおり同期する。
      lastInitIdRef.current = syncChatId;
      lastMsgSignatureRef.current = currentMsgSignature;
      return;
    }

    if (hasChatIdChanged) {
      // 旧 chat 文脈でキュー済みの待機を先に失効させる。
      invalidateQueue(new Error("Chat context switched"));
    }

    if (
      hasMessageSignatureChanged &&
      !hasChatIdChanged &&
      hasInFlightPersistence
    ) {
      // 同一 chat かつ永続化中は、ローカルの楽観的状態を維持する。
      // 現在の永続化サイクル完了後、次回 render で再評価する。
      return;
    }

    if (hasChatIdChanged || hasMessageSignatureChanged) {
      setMessages(effectiveInitialMessages);
      resetPersistenceTracking(effectiveInitialMessages);
    }

    // 次回差分判定の基準を更新する。
    if (syncChatId !== lastInitIdRef.current) {
      lastInitIdRef.current = syncChatId;
    }
    lastMsgSignatureRef.current = currentMsgSignature;
  }, [
    activeChatId,
    effectiveInitialMessages,
    invalidateQueue,
    pendingCreatedChatId,
    pendingPersistenceCount,
    resetPersistenceTracking,
    setMessages,
  ]);
}
